<template>
  <div class="flex flex-col items-center px-2">
    <div
      class="flex whitespace-nowrap text-center text-lg font-semibold"
      v-if="!insetLabel && (label || $slots['label'])"
    >
      <slot name="label">
        {{ label }}
      </slot>
    </div>
    <div :class="['score-outer', size]">
      <div class="-mt-[5px] flex flex-col items-center" v-if="insetLabel">
        <div
          :class="[
            'score-value with-label',
            size,
            { 'z-10 !text-primary-900': solidDial },
            { 'w-10 text-center !text-[#CCCCCC]': mystery }
          ]"
        >
          {{ mystery ? '?' : displayedScore }}
        </div>
        <div
          :class="['inset-label -mt-3', { 'z-10 text-primary-900': solidDial }]"
          v-if="insetLabel"
        >
          {{ label }}
        </div>
      </div>
      <div
        :class="['score-value', size, { mystery }, { 'z-10 !text-primary-900': solidDial }]"
        v-else
      >
        {{ displayedScore }}
      </div>
      <div :class="['track', size, { mystery }, usedThickness]"></div>
      <div v-if="solidDial" :class="['track', size, usedThickness, 'bg-white/90']"></div>
      <div
        :class="['progress-wheel', { mystery }, size, usedThickness]"
        :style="mystery ? {} : wheelStyle"
      ></div>
      <div
        :class="['progress-wheel', size, { mystery }, usedThickness]"
        :style="
          mystery
            ? {}
            : {
                ...wheelStyle,
                transform: `rotate(${partialSegmentRotation}deg)`
              }
        "
      ></div>
    </div>
  </div>
</template>

<script setup lang="ts">
  import { linearProjection } from '~/utils/math'

  const props = withDefaults(
    defineProps<{
      score: number
      label?: string
      size?: 'small' | 'normal' | 'large' | 'extra-large'
      solidDial?: boolean
      trackColor?: string
      bandThickness?: 'thin' | 'normal' | 'thick'
      overrideColor?: string
      insetLabel?: boolean
      mystery?: boolean
    }>(),
    {
      trackColor: '#EEF0F1'
    }
  )

  const { flag } = FeatureFlags.use()

  const scoreConfig = computed(() => flag('plan-score-config').value)

  const usedThickness = computed(() => `band-${props.bandThickness}`)

  const usedTrackColor = computed(() => (props.mystery ? '#FFFFFF' : props.trackColor))

  const scoreColor = computed(() => {
    if (!!props.overrideColor) {
      return props.overrideColor
    }

    const idx = _findLastIndex(scoreConfig.value.tiers, (val) => val.min <= props.score)

    return idx === -1
      ? _last(scoreConfig.value?.tiers)?.level
      : scoreConfig.value?.tiers?.[idx]?.level
  })

  const displayedScore = computed(() => _clamp(props.score, 0, 99))

  const totalDegrees = computed(() => 360 * (props.score / 100))

  const wheelStyle = computed(() => ({
    borderTopColor: totalDegrees.value < 90 ? 'transparent' : scoreColor.value,
    borderRightColor: totalDegrees.value < 180 ? 'transparent' : scoreColor.value,
    borderBottomColor: totalDegrees.value < 270 ? 'transparent' : scoreColor.value,
    borderLeftColor: totalDegrees.value < 360 ? 'transparent' : scoreColor.value,
    transform: `rotate(45deg)`
  }))

  const partialSegmentRotation = computed(() => {
    const addedDegrees = totalDegrees.value - _floor(totalDegrees.value / 90) * 90

    return linearProjection(addedDegrees, 0, 90, 45, 135)
  })
</script>

<style scoped>
  .progress-wheel {
    @apply absolute h-[60px] w-[60px] rounded-full border-[6px];

    &.small {
      @apply h-[50px] w-[50px];
    }

    &.large {
      @apply h-[70px] w-[70px];
    }

    &.extra-large {
      @apply h-[80px] w-[80px];
    }

    &.band-thin {
      @apply border-[5px];
    }

    &.band-thick {
      @apply border-[7px];
    }

    &.mystery {
      @apply !border-dashed border-[#D9DCDD] bg-white;
    }
  }

  .score-outer {
    @apply relative flex h-[60px] items-center justify-center;

    &.small {
      @apply h-[50px];
    }

    &.large {
      @apply h-[70px];
    }

    &.extra-large {
      @apply h-[80px];
    }
  }

  .track {
    @apply absolute h-[60px] w-[60px] rounded-full border-[6px];

    border-color: v-bind(usedTrackColor);

    &.small {
      @apply h-[50px] w-[50px];
    }

    &.large {
      @apply h-[70px] w-[70px];
    }

    &.extra-large {
      @apply h-[80px] w-[80px];
    }

    &.band-thin {
      @apply border-[5px];
    }

    &.band-thick {
      @apply border-[7px];
    }
  }

  .score-value {
    @apply pb-0.5 text-5xl font-bold tracking-tighter;

    &.with-label {
      @apply mt-[3px] -translate-x-[1px];
    }

    &.small {
      @apply text-3xl;
    }

    &.extra-large {
      @apply text-[44px];
    }
  }

  .inset-label {
    @apply text-xs font-semibold;
  }
</style>
